<!--

  ### Template Mode

    a. `props.name` reusing <ui-icon>
    b. `slots.common-icon` by integrating a `components/common/icon

-->
<template>
  <button
    v-bind="$attrs"
    :class="[
      'ui-button-icon',
      {
        'ui-button-icon--disabled': status === UI.STATUS.DISABLED,
        'ui-button-icon--idled': status === UI.STATUS.IDLED,
        'ui-button-icon--squared': square,
        'ui-button-icon--no-animation': noAnimation,
      },
      variant ? `ui-button-icon--${variant}` : '',
      `ui-button-icon--focus-ring-${focusRing}`,
      focusRingVariant ?
        `ui-button-icon--focus-ring-variant-${focusRingVariant}` :
        '',
      `ui-button-icon--${elevation}`,
      `ui-button-icon--${size}`,
      `ui-button-icon--overlay-${overlaySize_}`,
    ]"
    :disabled="hasDisabledStatusProp"
    :type="type"
    v-on="{
      click: onClick_,
    }"
  >
    <ripple-component
      :status="ripple && ! hasDisabledStatusProp ?
        UI.STATUS.ENABLED :
        UI.STATUS.DISABLED
      "
      variant="dark"
      class="ui-button-icon__ripple"
    >
      <span
        v-if="templateMode === 'common_icon'"
        class="ui-button-icon__inner"
      >
        <slot name="common-icon" />
      </span>

      <icon-component
        v-if="templateMode === 'icon_component'"
        :name="name!"
        :theme="theme"
        class="ui-button-icon__inner"
      />
    </ripple-component>
  </button>
</template>

<style lang="sass">
.ui-button-icon
  $self: &

  display: inline-block
  user-select: none
  background-color: initial // fix "white" background color in some browsers
  position: relative
  line-height: 0 // avoid anamorphosis due to "font-size"
  isolation: isolate

  &__inner
    @include clickable
    z-index: 1 // above overlay pseudo element
    position: relative
    overflow: hidden
    vertical-align: sub // allow better vertical alignement

  &__ripple
    @include clickable
    border-radius: 50%
    z-index: 1 // above overlay pseudo element

  /// Overlay preparation
  ///
  &::before
    @include virtual-content
    position: absolute
    border-radius: 50%
    background-color: transparent
    z-index: -2

  /// Square
  ///
  &--squared::before,
  &--squared &__ripple
    border-radius: $btn-border-radius

  /// Variants
  ///
  @each $theme, $value in $palette-semantic

    &--#{$theme}
      color: $value

  /// Elevations
  ///
  @each $theme, $value in $palette-semantic

    &--level-0:active#{&}--#{$theme} &__inner,
    &--level-0:focus#{&}--#{$theme} &__inner,
    &--level-0:hover#{&}--#{$theme} &__inner
      color: darken($value, 15%)

  &--level-1:active::before,
  &--level-1:focus::before,
  &--level-1:hover::before
    background-color: $btn-overlay-bg

  &--level-2::before
    background-color: $btn-overlay-bg

  &--level-2:active::before,
  &--level-2:focus::before,
  &--level-2:hover::before
    background-color: $btn-overlay-bg-hover

  /// State variations
  ///
  &[disabled]
    @include unclickable

  &--disabled
    opacity: $button-disable-opacity
  &--disabled#{&}--level-1:active::before,
  &--disabled#{&}--level-1:focus::before,
  &--disabled#{&}--level-1:hover::before
    content: initial
  &--disabled#{&}--level-2:active::before,
  &--disabled#{&}--level-2:focus::before,
  &--disabled#{&}--level-2:hover::before
    background-color: $btn-overlay-bg

  /// Sizing
  ///
  &--sm
    font-size: 17px
  &--md
    font-size: $icon-default-size
  &--lg
    font-size: 30px

  &--overlay-sm::before,
  &--overlay-sm &__ripple
    @include inset( space-x(-.5) )
  &--overlay-md::before,
  &--overlay-md &__ripple
    @include inset( space-x(-1) )
  &--overlay-lg::before,
  &--overlay-lg &__ripple
    @include inset( space-x(-1.25) )

  /// Animation
  ///
  &:not(&--no-animation)::before
    transition: $transition-base

  /// Focus ring
  ///
  /// Dedicated ::after layer — elevation overlay stays on ::before.

  &--focus-ring-active::after,
  &--focus-ring-always::after
    @include virtual-content
    position: absolute
    border-radius: 50%
    background-color: transparent
    z-index: -1
    pointer-events: none

  &--squared#{&}--focus-ring-active::after,
  &--squared#{&}--focus-ring-always::after
    border-radius: $btn-border-radius

  &--overlay-sm#{&}--focus-ring-active::after,
  &--overlay-sm#{&}--focus-ring-always::after
    @include inset(space-x(-.5))

  &--overlay-md#{&}--focus-ring-active::after,
  &--overlay-md#{&}--focus-ring-always::after
    @include inset(space-x(-1))

  &--overlay-lg#{&}--focus-ring-active::after,
  &--overlay-lg#{&}--focus-ring-always::after
    @include inset(space-x(-1.25))

  &--focus-ring-active::after
    box-shadow: 0 0 0 0 transparent

  &--focus-ring-always::after
    transition: none

  &:not(&--no-animation)#{&}--focus-ring-active::after
    transition: $transition-base

  &--focus-ring-active:not(#{&}--disabled)
    &:focus::after,
    &:active::after
      box-shadow: 0 0 0 2px mix-alpha($focus-ring-color, 50%)

  &--focus-ring-always:not(#{&}--disabled)::after
    box-shadow: 0 0 0 2px mix-alpha($focus-ring-color, 50%)

  @include focus-ring-variant-theme

</style>

<script lang="ts">
import RippleComponent from '~/components/ui/Ripple.vue'
import IconComponent, { IconProps } from '~/components/ui/Icon.vue'
import type { ButtonHTMLAttributes, SlotsType } from 'vue'
import { includes, not, notEmpty } from '~/utils/lib/core/prelude'
import type { ELEVATION, FOCUS_RING, SIZE, STATUS, VARIANT } from '~/utils/lib/layout/ui'

type TEMPLATE_MODE = "icon_component" | "common_icon"

export const ButtonIconProps = {
  ...IconProps,
  name: {
    type: String,
    required: false
  },
  size: {
    type: String as PropType<SIZE>,
    required: false,
    default: 'md' satisfies SIZE
  },
  status: {
    type: String as PropType<STATUS>,
    required: false,
    default: 'enabled' satisfies STATUS
  },
  type: {
    type: String as PropType<ButtonHTMLAttributes["type"]>,
    required: false,
    default: 'button'
  },
  elevation: {
    type: String as PropType<ELEVATION>,
    required: false,
    default: 'level-1' satisfies ELEVATION
  },
  variant: {
    type: String as PropType<VARIANT>,
    required: false,
    default: undefined
  },
  square: {
    type: Boolean,
    required: false,
    default: false
  },
  focusRing: {
    type: String as PropType<FOCUS_RING>,
    required: false,
    default: 'none' satisfies FOCUS_RING
  },
  focusRingVariant: {
    type: String as PropType<VARIANT | undefined>,
    required: false,
    default: undefined
  },
  ripple: {
    type: Boolean,
    required: false,
    default: false
  },
  noAnimation: {
    type: Boolean,
    required: false,
    default: false
  },
  overlaySize: {
    type: String as PropType<SIZE>,
    required: false,
    default: undefined,
  },
} as const
  // @link https://github.com/vuejs/composition-api/issues/106

export default defineComponent({
  components: { RippleComponent, IconComponent },
  props: ButtonIconProps,
  /// @XXX "vue3" Options API and deprecated $listeners on $attrs horrendous
  ///      design
  ///    ↳ consequence: Options API put its event to shambles troughout the
  ///      the $attrs
  ///         a) thus passing an event "@myEvent" to the component results
  ///            in a `$attrs.onMyEvent`
  ///         b) so using a `v-bind="...$attrs"` on a child component will
  ///            transmit this `onMyEvent` prop
  ///         c) BUT if a `@myEvent` is also added in this child component,
  ///            it will **double** the execution: one from the `$attrs.
  ///            myEvent` setup on the component itself ; another one transmit
  ///            to child component (no resolution made)
  ///    ↳ workaround: as the discussion states → the API bears is smelly,
  ///      and is a major break from v2 to v3 → as comments state, solution
  ///      are empirical depending on the context... [1]
  ///    ↳ empirical surgery: opting for the emit event override [2]
  ///
  /// @link https://github.com/vuejs/rfcs/discussions/397 [1]
  /// @link https://github.com/vuejs/rfcs/discussions/397#discussioncomment-10893510 [2]
  emits: [ 'click' ] as const,
  slots: Object as SlotsType<{
    'common-icon'?: true,
  }>,
  computed: {
    templateMode() :TEMPLATE_MODE {
      switch (true) {
        case notEmpty(this.name): return 'icon_component'
        default:                  return 'common_icon'
      }
    },
    hasDisabledStatusProp() {
      return includes(
          this.status,
          [
            'disabled',
            'idled',
            'deffered',
          ] satisfies STATUS[]
        )
    },
    hasPreventClickStatusType() {
      return includes(
          this.status,
          [
            'disabled',
            'idled',
            'deffered',
            'muted',
          ] satisfies STATUS[]
        )
    },
    overlaySize_() {
      return this.overlaySize ?? this.size
    },
  },
  methods: {
    onClick_(event: Event) {
      if (not(this.hasPreventClickStatusType))
        this.$emit('click', event)
    },
  }
})
</script>
